import { HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CertificateDTO } from '@domains/course/domain/models/certificate.dto';
import { compare } from 'fast-json-patch';
import { CompanySectionDTO, SaveCompanySectionDTO } from '../models/backoffice/company-section.interface';
import { map, Observable, tap } from 'rxjs';
import { BackofficeCompanyService } from '../infrastructure/backoffice/backoffice-company.service';
import { CompanyEmailTemplateDTO } from '../models/company-email-template.interface';
import { EmailTemplateKindEnum } from '../models/enums/email-template-kind.enum';
import { InviteService } from '../infrastructure/invite.service';
import { Invitation } from '../models/invitation.interface';
import { UserDTO } from '../models/users.dto';
import { CreateInvitation } from '../models/create-invitation.dto';
import { IdentityFacade } from './identity.facade';
import { BackofficeCompanyDetailDTO } from '../models/company-backoffice-detail.interface';
import { UserFormModel } from '../models/user-form.model';
import { BackofficeUserService } from '../infrastructure/backoffice/backoffice-user.service';
import { UserDetailBackofficeModel } from '../models/user-detail-backoffice.model';
import { BackofficeCompanyCourseDTO } from '@domains/course/domain/models/company-course.interface';
import { BackofficeCompanyCourseService } from '../infrastructure/backoffice/backoffice-company-course.service';
import { BackofficeStudentService } from '../infrastructure/backoffice/backoffice-student.service';
import { BackofficeStudentTestResultService } from '../infrastructure/backoffice/backoffice-student-test-result.service';
import { UserRoleEnum } from '../models/enums/user-role.enum';
import { ReminderResult } from '../models/reminder-result.interface';
import { ReleaseAccessApi } from '@domains/course/domain/models/release-access-api.interface';
import { ReleaseAccessOptionsDTO, ReleaseAccessPreviewDTO, ReleaseAccessRequestDTO } from '@domains/course/domain/models/release-access.dto';

@Injectable({ providedIn: 'root' })
export class BackofficeCompanyFacade implements ReleaseAccessApi {

  constructor(
    private identityFacade: IdentityFacade,
    private companyService: BackofficeCompanyService,
    private companyCourseService: BackofficeCompanyCourseService,
    private userService: BackofficeUserService,
    private invitationService: InviteService,
    private backofficeStudentService: BackofficeStudentService,
    private studentTestResultService: BackofficeStudentTestResultService,
  ) { }

  loadCompanyInfoIntoIdentity(id: string): Observable<BackofficeCompanyDetailDTO> {
    return this.companyService.getCompany(id).pipe(
      tap((company) => {
        this.identityFacade.setCurrentCompany(company);
      })
    );
  }


  setCurrentCompany(companyId: string): void {
    this.getCompany(companyId).pipe((
      tap((company) => {
        this.identityFacade.setCurrentCompany(company)
      })
    )).subscribe();
  }

  getCompany(id: string): Observable<BackofficeCompanyDetailDTO> {
    return this.companyService.getCompany(id);
  }

  inviteUser(data: CreateInvitation): Observable<undefined> {
    return this.invitationService.inviteUser(data);
  }

  checkUser(token: string | null): Observable<Invitation> {
    const validToken = token ? encodeURIComponent(token) : 'null';
    return this.invitationService.checkUser(validToken);
  }

  // TODO: StudentModel
  submitStudentsIntoCourse(companyCourseId: string, userId: string): Observable<any> {
    return this.companyCourseService.addUser(companyCourseId, userId);
  }

  getCompanyCourses(companyId: string): Observable<BackofficeCompanyCourseDTO[]> {
    return this.companyService.getCompanyCourses(companyId);
  }


  updateCompany(company: BackofficeCompanyDetailDTO): Observable<BackofficeCompanyDetailDTO> {
    return this.companyService.updateCompany(company);
  }

  getUsers(companyId: string): Observable<UserDTO[]> {
    return this.companyService.getUsers(companyId);
  }

  getUser(userId: string): Observable<UserDetailBackofficeModel> {
    return this.userService.getUser(userId);
  }

  createUser(companyId: string, user: UserFormModel): Observable<UserDTO> {
    return this.companyService.createUser(companyId, user);
  }

  createUsers(companyId: string, users: UserFormModel[]): Observable<UserDTO[]> {
    return this.companyService.createUsers(companyId, users);
  }

  /**
   * Uloží změněné údaje zaměstnance jako JSON Patch — posílá se jen to, co se liší
   * (EL-2297: dřív skládaly `compare()` komponenty samy).
   */
  updateUser(before: UserDetailBackofficeModel, after: UserDetailBackofficeModel): Observable<UserDetailBackofficeModel> {
    return this.userService.patch(compare(before, after), before.id);
  }

  /**
   * Deaktivuje nebo obnoví **členství v téhle firmě** — ne celý účet, ten patří
   * vydavateli (EL-2287). Dřív to tři místa skládala každé po svém.
   */
  setMembershipDisabled(userId: string, disabled: boolean): Observable<UserDetailBackofficeModel> {
    return this.userService.patch(compare({ disabled: !disabled }, { disabled }), userId);
  }

  setUserRoles(userId: string, roles: string[]): Observable<UserDetailBackofficeModel> {
    return this.userService.setRoles(userId, roles);
  }

  deleteUser(userId: string): Observable<void> {
    return this.userService.deleteUser(userId);
  }

  sendCompanyReminder(companyId: string, audience: 'unregistered' | 'courseUnfinished'): Observable<ReminderResult> {
    return this.companyService.sendReminder(companyId, audience);
  }

  sendCourseReminder(companyCourseId: string): Observable<ReminderResult> {
    return this.companyCourseService.sendReminder(companyCourseId);
  }

  sendCourseReminderUnregistered(companyCourseId: string): Observable<ReminderResult> {
    return this.companyCourseService.sendReminderUnregistered(companyCourseId);
  }

  recomputeCourseAccess(companyCourseId: string): Observable<void> {
    return this.companyCourseService.recomputeAccess(companyCourseId);
  }

  getReleaseAccessOptions(companyCourseId: string): Observable<ReleaseAccessOptionsDTO> {
    return this.companyCourseService.getReleaseAccessOptions(companyCourseId);
  }

  previewReleaseAccess(companyCourseId: string, articleId: string, onlyBehind: boolean): Observable<ReleaseAccessPreviewDTO> {
    return this.companyCourseService.previewReleaseAccess(companyCourseId, articleId, onlyBehind);
  }

  releaseAccess(companyCourseId: string, request: ReleaseAccessRequestDTO): Observable<void> {
    return this.companyCourseService.releaseAccess(companyCourseId, request);
  }

  sendRegisterEmail(userId: string): Observable<void> {
    return this.userService.sendRegisterEmail(userId);
  }

  // CompanyCourse
  updateCompanyCourseEmail(course: BackofficeCompanyCourseDTO) {
    return this.companyService.updateEmail(course);
  }

  getCompanySections(companyCourseId: string): Observable<CompanySectionDTO[]> {
    return this.companyCourseService.getCompanySections(companyCourseId);
  }

  saveCompanySection(companyCourseId: string, data: SaveCompanySectionDTO): Observable<void> {
    return this.companyCourseService.saveCompanySection(companyCourseId, data);
  }

  updateCompanyCourse(companyCourse: BackofficeCompanyCourseDTO): Observable<BackofficeCompanyCourseDTO> {
    return this.companyCourseService.updateCompanyCourse(companyCourse);
  }

  uploadCompanyCourseCertificate(companyCourseId: string, languageIso: string, file: File): Observable<HttpEvent<CertificateDTO>> {
    return this.companyCourseService.uploadCertificate(companyCourseId, languageIso, file);
  }

  deleteCompanyCourseCertificate(companyCourseId: string, languageIso: string): Observable<void> {
    return this.companyCourseService.deleteCertificate(companyCourseId, languageIso);
  }

  getCompanyCourseCertificateLanguages(companyCourseId: string): Observable<string[]> {
    return this.companyCourseService.getCertificateLanguages(companyCourseId);
  }

  getCurrentCompanyStudents(): Observable<UserDTO[]> {
    return this.identityFacade.getCurrentCompany$.pipe(
      map(c => c?.users!.filter(user => user.assignedRoles.some(r => r.name === UserRoleEnum.Student))!,
      ));
  }

  getCompanyCourse(courseId: string): Observable<BackofficeCompanyCourseDTO> {
    return this.companyCourseService.getCompanyCourse(courseId);
  }

  getCertificateBackoffice(studentTestResultId: string): Observable<BlobPart> {
    return this.studentTestResultService.getCertificateBackoffice(studentTestResultId);
  }

  getEmailTemplateDefaults(companyId: string, kind: EmailTemplateKindEnum): Observable<CompanyEmailTemplateDTO[]> {
    return this.companyService.getEmailTemplateDefaults(companyId, kind);
  }

  uploadLogo(companyId: string, file: File): Observable<string> {
    return this.companyService.uploadLogo(companyId, file);
  }
  //TODO: model StudentModel
  updateStudentCourse(course: any): Observable<any> {
    return this.backofficeStudentService.update(course);
  }
}
